import * as React from 'react';

import { ButtonShape, Row } from 'farmhub-ui-core';

import { Action } from 'components';
import { ArrowRightIcon } from 'components/Icon';
import { ArrowLeftIcon } from 'components/Icon/ArrowLeftIcon';
import { DEFAULT_WIDTH_AS_NUMBER } from 'utils/constants';

import {
  ButtonWrapper,
  IconButtonStyles,
  SlideShowWrapper,
  Wrapper,
} from './styles';

const SlideShow = ({
  children,
  width = DEFAULT_WIDTH_AS_NUMBER,
  spaceScroll = width,
  background = '#fff',
  spacing = 0,
  padding = 0,
  wrapperPadding,
}: SlideShowProps): JSX.Element => {
  const refSlide = React.useRef<HTMLDivElement>(null);
  const refPrev = React.useRef<HTMLDivElement>(null);
  const refNext = React.useRef<HTMLDivElement>(null);
  const refDiv = React.useRef<HTMLDivElement>(null);
  const [slideShowWidth, setSlideShowWidth] = React.useState<number>(width);
  const [slideShowSpaceScroll, setSlideShowSpaceScroll] =
    React.useState<number>(spaceScroll);

  const changeWidth = () => {
    setSlideShowWidth(
      refDiv.current ? refDiv.current.offsetWidth : DEFAULT_WIDTH_AS_NUMBER
    );
    setSlideShowSpaceScroll(
      refDiv.current ? refDiv.current.offsetWidth : DEFAULT_WIDTH_AS_NUMBER
    );
  };

  React.useLayoutEffect(() => {
    if (width === DEFAULT_WIDTH_AS_NUMBER) {
      changeWidth();
    }
  }, [width]);

  React.useLayoutEffect(() => {
    window.addEventListener('resize', changeWidth);
    return () => window.removeEventListener('resize', changeWidth);
  }, []);

  React.useEffect(() => {
    setTimeout(() => {
      if (refPrev.current && refNext.current && refSlide.current) {
        if (refSlide.current?.scrollWidth <= slideShowWidth) {
          refNext.current.style.display = 'none';
        } else {
          refNext.current.style.display = 'flex';
        }

        refPrev.current.style.display = 'none';
      }
    }, 500);
  }, [slideShowWidth, refSlide.current?.scrollWidth]);

  const nextSlide = () => {
    if (
      refNext.current &&
      refSlide.current &&
      refSlide.current.scrollLeft + 2 * slideShowSpaceScroll + 12 + spacing >=
        refSlide.current.scrollWidth
    ) {
      refNext.current.style.display = 'none';
    }

    if (refSlide.current) {
      refSlide.current.scroll({
        top: 0,
        left: refSlide.current.scrollLeft + slideShowSpaceScroll + spacing,
        behavior: 'smooth',
      });
    }

    if (refPrev.current) {
      refPrev.current.style.display = 'flex';
    }
  };

  const prevSlide = () => {
    if (
      refPrev.current &&
      refSlide.current &&
      refSlide.current.scrollLeft - (slideShowSpaceScroll + spacing) <= 0
    ) {
      refPrev.current.style.display = 'none';
    }

    if (refSlide.current) {
      refSlide.current.scroll({
        top: 0,
        left: refSlide.current.scrollLeft - (slideShowSpaceScroll + spacing),
        behavior: 'smooth',
      });
    }

    if (refNext.current) {
      refNext.current.style.display = 'flex';
    }
  };

  return (
    <Row ref={refDiv} justifyContent="center">
      <Wrapper width={slideShowWidth - padding}>
        <SlideShowWrapper
          ref={refSlide}
          background={background}
          padding={wrapperPadding}
        >
          {children}
        </SlideShowWrapper>
        <ButtonWrapper ref={refPrev} className="prev-button" type="PREV">
          <Action
            buttonProps={{
              shape: ButtonShape.Circle,
              icon: { children: <ArrowLeftIcon width={12} /> },
              width: '36px',
              height: '36px',
              styles: IconButtonStyles,
            }}
            onClick={prevSlide}
          />
        </ButtonWrapper>
        <ButtonWrapper ref={refNext} className="next-button" type="NEXT">
          <Action
            buttonProps={{
              shape: ButtonShape.Circle,
              icon: { children: <ArrowRightIcon width={12} /> },
              width: '36px',
              height: '36px',
              styles: IconButtonStyles,
            }}
            onClick={nextSlide}
          />
        </ButtonWrapper>
      </Wrapper>
    </Row>
  );
};

export default SlideShow;

export interface SlideShowProps {
  width?: number;
  spaceScroll?: number;
  spacing: number;
  background?: string;
  wrapperPadding?: string;
  padding?: number;
  children: React.ReactNode | React.ReactNode[];
}
